import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Card, Field, SelectField } from '@pixlide/ui';
import { useCreateEstablishment, useCreateScreen, useStartScreenPairing, type PairingCode } from '@/api/queries/screens';
import { FormAlert } from '@/features/auth/AuthLayout';
import { describeScreenError } from '@/features/screens/screen-errors';

/** Bornes reprises du Domain (`Screen.MaxNameLength`, `Establishment.MaxNameLength`). */
const SCREEN_NAME_MAX = 100;
const ESTABLISHMENT_NAME_MAX = 150;

/**
 * Résolutions proposées. Le client raisonne en « paysage / portrait », pas en pixels : c'est
 * l'orientation qui décide quels formats de vidéo l'écran peut diffuser
 * (`BroadcastCompatibilityPolicy`). La résolution exacte reste modifiable plus tard.
 */
const ORIENTATIONS = {
  landscape: { label: 'Paysage (TV posée à l’horizontale)', width: 1920, height: 1080 },
  portrait: { label: 'Portrait (totem, écran vertical)', width: 1080, height: 1920 },
} as const;

/**
 * Règles dupliquées du serveur pour un retour immédiat ; le serveur reste la seule barrière
 * réelle. Les champs d'établissement ne sont exigés que lorsqu'aucun n'existe encore.
 */
function declareScreenSchema(needsEstablishment: boolean) {
  const required = (message: string) => z.string().trim().min(1, message);

  return z
    .object({
      establishmentId: z.string(),
      establishmentName: z.string().trim().max(ESTABLISHMENT_NAME_MAX),
      street: z.string().trim(),
      postalCode: z.string().trim(),
      city: z.string().trim(),
      name: required("Le nom de l'écran est obligatoire.").max(SCREEN_NAME_MAX),
      playbackMode: z.enum(['0', '1']),
      orientation: z.enum(['landscape', 'portrait']),
    })
    .superRefine((values, ctx) => {
      if (!needsEstablishment) {
        if (values.establishmentId === '') {
          ctx.addIssue({ code: 'custom', path: ['establishmentId'], message: 'Choisissez un établissement.' });
        }

        return;
      }

      const missing: Array<[keyof typeof values, string]> = [
        ['establishmentName', "Le nom de l'établissement est obligatoire."],
        ['street', 'La rue est obligatoire.'],
        ['city', 'La ville est obligatoire.'],
      ];

      for (const [field, message] of missing) {
        if (values[field] === '') {
          ctx.addIssue({ code: 'custom', path: [field], message });
        }
      }

      if (!/^\d{5}$/.test(values.postalCode)) {
        ctx.addIssue({ code: 'custom', path: ['postalCode'], message: 'Le code postal doit contenir 5 chiffres.' });
      }
    });
}

type DeclareScreenInput = z.infer<ReturnType<typeof declareScreenSchema>>;

interface DeclareScreenPanelProps {
  readonly establishments: ReadonlyArray<{ id: string; name: string }>;
  readonly onDeclared: (screen: { id: string; name: string }, code: PairingCode) => void;
  readonly onCancel: () => void;
}

/**
 * Déclaration d'un écran, enchaînée avec la génération de son code d'appairage : le client
 * qui déclare un écran est en train de l'installer, il a besoin du code dans la foulée.
 *
 * Sans établissement existant, le premier est créé dans le même formulaire plutôt que de
 * renvoyer le client vers une autre page en pleine installation.
 */
export function DeclareScreenPanel({ establishments, onDeclared, onCancel }: DeclareScreenPanelProps) {
  const needsEstablishment = establishments.length === 0;

  const createEstablishment = useCreateEstablishment();
  const createScreen = useCreateScreen();
  const startPairing = useStartScreenPairing();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<DeclareScreenInput>({
    resolver: zodResolver(declareScreenSchema(needsEstablishment)),
    defaultValues: {
      establishmentId: establishments.length === 1 ? establishments[0]!.id : '',
      establishmentName: '',
      street: '',
      postalCode: '',
      city: '',
      name: '',
      playbackMode: '0',
      orientation: 'landscape',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    // Erreurs remontées par l'état des mutations (affichées par FormAlert) : on interrompt
    // simplement l'enchaînement à la première étape en échec.
    try {
      const establishmentId = needsEstablishment
        ? await createEstablishment.mutateAsync({
            name: values.establishmentName,
            street: values.street,
            postalCode: values.postalCode,
            city: values.city,
            countryCode: 'FR',
          })
        : values.establishmentId;

      const { width, height } = ORIENTATIONS[values.orientation];

      const screenId = await createScreen.mutateAsync({
        establishmentId,
        name: values.name,
        playbackMode: values.playbackMode === '1' ? 1 : 0,
        width,
        height,
      });

      const code = await startPairing.mutateAsync(screenId);

      onDeclared({ id: screenId, name: values.name }, code);
    } catch {
      // Déjà porté par l'état d'erreur de la mutation concernée.
    }
  });

  const failure = createEstablishment.error ?? createScreen.error ?? startPairing.error;

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-ink-900">Déclarer un écran</h3>
      <p className="mt-1 text-sm text-ink-500">
        Le code d'appairage s'affichera juste après : gardez votre TV ou votre boîtier à portée de main.
      </p>

      <form onSubmit={onSubmit} noValidate className="mt-5 flex flex-col gap-4">
        {failure ? <FormAlert message={describeScreenError(failure)} /> : null}

        {needsEstablishment ? (
          <fieldset className="flex flex-col gap-3">
            <legend className="mb-2 text-sm font-medium text-ink-700">Votre établissement</legend>
            <Field label="Nom de l'établissement" required autoFocus error={errors.establishmentName?.message} {...register('establishmentName')} />
            <Field label="Rue" required autoComplete="street-address" error={errors.street?.message} {...register('street')} />
            <div className="grid gap-3 sm:grid-cols-[10rem_1fr]">
              <Field label="Code postal" required inputMode="numeric" autoComplete="postal-code" error={errors.postalCode?.message} {...register('postalCode')} />
              <Field label="Ville" required autoComplete="address-level2" error={errors.city?.message} {...register('city')} />
            </div>
          </fieldset>
        ) : (
          <SelectField label="Établissement" required error={errors.establishmentId?.message} {...register('establishmentId')}>
            <option value="" disabled>
              Choisir un établissement
            </option>
            {establishments.map((establishment) => (
              <option key={establishment.id} value={establishment.id}>
                {establishment.name}
              </option>
            ))}
          </SelectField>
        )}

        <Field
          label="Nom de l'écran"
          required
          placeholder="Écran comptoir, Menu salle…"
          autoFocus={!needsEstablishment}
          error={errors.name?.message}
          {...register('name')}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <SelectField label="Mode de diffusion" error={errors.playbackMode?.message} {...register('playbackMode')}>
            <option value="0">TV connectée (application Pixlide)</option>
            <option value="1">Boîtier Pixlide</option>
          </SelectField>

          <SelectField label="Orientation" error={errors.orientation?.message} {...register('orientation')}>
            {Object.entries(ORIENTATIONS).map(([value, orientation]) => (
              <option key={value} value={value}>
                {orientation.label}
              </option>
            ))}
          </SelectField>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Annuler
          </Button>
          <Button type="submit" isLoading={isSubmitting}>
            Déclarer et appairer
          </Button>
        </div>
      </form>
    </Card>
  );
}
